import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  getAutorizacoesPendentes,
  decidirAutorizacao,
} from '../../../api/orcamentoCiliaApi';
import { OrcamentoCilia } from '../../../interfaces/OrcamentoCilia';
import { queryKeys } from '../../../infrastructure/react-query/queryKeys';
import { useIsTi } from '../../../hooks/useUserType';
import ArrowLeftOrange from '../../../img/arrow-left-orange.svg';
import ArrowLeftGray from '../../../img/arrow-left-gray.svg';
import ArrowRightOrange from '../../../img/arrow-right-orange.svg';
import ArrowRightGray from '../../../img/arrow-right-gray.svg';
import '../../recepcao/pages/Recepcao.css';
import '../pages/OrcamentoCiliaList.css';
// reaproveita o modal (import-xml-*) e os botões/inputs (cilia-*) já existentes
import './ImportXmlModal.css';
import '../pages/OrcamentoCiliaDetail.css';

// Aba "Autorizações" da tela Orçamentos Cilia (decisão de 30/07/2026 — sem tela própria).
// Fila de autorização de complementos (decisão do Victor, 29/07/2026): do 4º
// XML de complemento em diante o import fica aguardando o Chefe de Oficina.
// A tela vive no front compartilhado (SOAC + módulo dividem o frontend) e
// fala direto com o backend do módulo (JWT compartilhado, ADR 0014).
const AutorizacoesTab: React.FC = () => {
  const queryClient = useQueryClient();
  const isTi = useIsTi();

  const [page, setPage] = useState(1);
  const limit = 10;

  // Decisão em andamento (modal de confirmação explicativo — padrão do at-18)
  const [decisaoTarget, setDecisaoTarget] = useState<{ orcamento: OrcamentoCilia; decisao: 'APROVADA' | 'NEGADA' } | null>(null);
  const [nota, setNota] = useState('');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: queryKeys.orcamentoCilia.autorizacoesPendentes({ page, limit }),
    queryFn: () => getAutorizacoesPendentes({ page, limit }),
  });

  const decidirMutation = useMutation({
    mutationFn: ({ id, decisao, notaTexto }: { id: string; decisao: 'APROVADA' | 'NEGADA'; notaTexto?: string }) =>
      decidirAutorizacao(id, { decisao, nota: notaTexto }),
    onSuccess: (_r, vars) => {
      toast.success(
        vars.decisao === 'APROVADA'
          ? 'Complemento autorizado — a conferência foi liberada e o orçamentista notificado.'
          : 'Complemento negado — o orçamento foi ignorado e o orçamentista notificado.',
      );
      setDecisaoTarget(null);
      setNota('');
      queryClient.invalidateQueries({ queryKey: ['orcamento-cilia'] });
    },
    onError: (err: any) => {
      toast.error(err.message || 'Erro ao registrar a decisão.');
    },
  });

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / limit)) : 1;

  return (
    <>

      <div className="QuantidadeDeCarros">
        <div className="Frame1000004037" style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex' }}>
          <div className="CtaQuantidadeDeCarros">
            <div className="Frame1000003987">
              <div className="ComponentName">
                {isLoading
                  ? 'Carregando...'
                  : isError
                    ? 'Erro ao carregar dados'
                    : `${data?.total ?? 0} complemento(s) aguardando autorização`}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          width: '100%',
          paddingTop: 30,
          background: 'var(--tema-superficie, white)',
          borderRadius: 16,
          border: '1px var(--tema-linha-2, #CACACA) solid',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          display: 'flex',
          marginBottom: 20,
        }}
      >
        <div className="TabelaScroll" style={{ width: '100%' }}>
          {isLoading ? (
            <div style={{ padding: '60px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
              <div className="spinner"></div>
              <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: 'var(--tema-texto-3, #6b6b6b)' }}>Carregando fila...</span>
            </div>
          ) : isError ? (
            <div style={{ padding: '60px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
              <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#dc2626' }}>
                Erro ao carregar a fila. Por favor, tente novamente.
              </span>
              <div className="CtaBig MouseHover" onClick={() => refetch()}>
                <div className="Frame1000003987">
                  <div className="ComponentName">Tentar Novamente</div>
                </div>
              </div>
            </div>
          ) : !data || data.data.length === 0 ? (
            <div style={{ padding: '60px 20px', display: 'flex', justifyContent: 'center' }}>
              <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: 'var(--tema-texto-3, #6b6b6b)' }}>
                Nenhum complemento aguardando autorização.
              </span>
            </div>
          ) : (
            <div
              className="TabelaContent"
              style={{
                // Todas as colunas flexíveis (minmax + fr): a folga se
                // distribui entre elas em vez de virar um vazio gigante
                // entre Veículo e Importado em
                ['--table-columns' as any]:
                  'minmax(110px, 0.8fr) minmax(140px, 0.9fr) minmax(120px, 0.8fr) minmax(240px, 1.8fr) minmax(170px, 1fr) minmax(190px, 1fr)',
                width: '100%',
                minWidth: '990px',
              }}
            >
              <div className="Tabela TabelaHeader">
                <div className="Placa">
                  <div className="TTulo">
                    <div className="Opcoes">Placa</div>
                    <div className="Opcoes">Nº Cilia</div>
                    <div className="Opcoes">Evento</div>
                    <div className="Opcoes">Veículo</div>
                    <div className="Opcoes">Importado em</div>
                    <div className="Opcoes">Ações</div>
                  </div>
                </div>
              </div>

              {data.data.map((orcamento) => (
                <div key={orcamento.id} className="Tabela">
                  <div className="Item">
                    <div className="Data" style={{ fontWeight: 600 }}>{orcamento.placa || '-'}</div>
                  </div>
                  <div className="Item">
                    <div className="Data">
                      {orcamento.numeroCilia} (v{orcamento.versaoCilia})
                    </div>
                  </div>
                  <div className="Item">
                    <div className="Data">{orcamento.numeroEvento}</div>
                  </div>
                  <div className="Item">
                    <div className="Data" style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                      {orcamento.dadosVeiculo?.nomeVeiculo || '—'}
                    </div>
                  </div>
                  <div className="Item">
                    <div className="Data">{formatDate(orcamento.createdAt)}</div>
                  </div>
                  <div className="Item">
                    <div className="Data" style={{ display: 'flex', gap: 8 }}>
                      {isTi ? (
                        <span className="value-read-only">somente leitura</span>
                      ) : (
                        <>
                          <button
                            className="cilia-btn"
                            style={{ padding: '6px 14px' }}
                            onClick={() => { setDecisaoTarget({ orcamento, decisao: 'APROVADA' }); setNota(''); }}
                          >
                            Aprovar
                          </button>
                          <button
                            className="cilia-btn secondary"
                            style={{ padding: '6px 14px' }}
                            onClick={() => { setDecisaoTarget({ orcamento, decisao: 'NEGADA' }); setNota(''); }}
                          >
                            Negar
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {data && data.total > limit && (
          <div className="PaginaO" style={{ borderTop: '1px solid var(--tema-linha, #EDEDED)', width: '100%', boxSizing: 'border-box' }}>
            <div className="Frame1000003950">
              <div
                className="Anterior"
                onClick={() => page > 1 && setPage((p) => p - 1)}
                style={{
                  cursor: page === 1 ? 'not-allowed' : 'pointer',
                  color: page === 1 ? '#BEBEBE' : '#ea5d0e',
                  fontWeight: page === 1 ? '400' : '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <img src={page === 1 ? ArrowLeftGray : ArrowLeftOrange} alt="Anterior" />
                Anterior
              </div>
            </div>
            <div className="Group20">
              <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: 'var(--tema-texto-2, #5E5E5E)' }}>
                Página <strong>{page}</strong> de {totalPages}
              </span>
            </div>
            <div className="Frame1000003949">
              <div
                className="PrXima"
                onClick={() => page < totalPages && setPage((p) => p + 1)}
                style={{
                  cursor: page === totalPages ? 'not-allowed' : 'pointer',
                  color: page === totalPages ? '#BEBEBE' : '#ea5d0e',
                  fontWeight: page === totalPages ? '400' : '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                Próxima
                <img src={page === totalPages ? ArrowRightGray : ArrowRightOrange} alt="Próxima" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de confirmação explicativo (padrão das confirmações de horas do at-18) */}
      {decisaoTarget && (
        <div className="import-xml-overlay" onClick={() => !decidirMutation.isPending && setDecisaoTarget(null)}>
          <div className="import-xml-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="import-xml-header">
              <h3>{decisaoTarget.decisao === 'APROVADA' ? 'Autorizar complemento' : 'Negar complemento'}</h3>
            </div>
            <div className="import-xml-body" style={{ fontSize: 14, lineHeight: 1.5 }}>
              <p>
                Orçamento <strong>{decisaoTarget.orcamento.numeroCilia}</strong> (v
                {decisaoTarget.orcamento.versaoCilia}) — placa <strong>{decisaoTarget.orcamento.placa}</strong>.
              </p>
              {decisaoTarget.decisao === 'APROVADA' ? (
                <p>
                  Ao aprovar, o complemento <strong>segue para a conferência normalmente</strong> e o orçamentista
                  que o importou será notificado de que o complemento foi autorizado.
                </p>
              ) : (
                <>
                  <p>
                    Ao negar, o orçamento é <strong>ignorado</strong> — o fluxo não continua e nenhuma peça deste
                    complemento será solicitada. O orçamentista será notificado com o motivo.
                  </p>
                  <label style={{ display: 'block', marginTop: 8 }}>
                    <strong>Motivo (obrigatório):</strong>
                    <textarea
                      className="cilia-input"
                      style={{ width: '100%', minHeight: 70, marginTop: 6, boxSizing: 'border-box' }}
                      maxLength={500}
                      value={nota}
                      onChange={(e) => setNota(e.target.value)}
                      placeholder="Explique por que este complemento não deve seguir"
                    />
                  </label>
                </>
              )}
            </div>
            <div className="import-xml-footer">
              <button
                className="import-xml-btn cancel"
                onClick={() => setDecisaoTarget(null)}
                disabled={decidirMutation.isPending}
              >
                Cancelar
              </button>
              <button
                className="import-xml-btn confirm"
                disabled={decidirMutation.isPending || (decisaoTarget.decisao === 'NEGADA' && !nota.trim())}
                onClick={() =>
                  decidirMutation.mutate({
                    id: decisaoTarget.orcamento.id,
                    decisao: decisaoTarget.decisao,
                    notaTexto: nota.trim() || undefined,
                  })
                }
              >
                {decidirMutation.isPending
                  ? 'Registrando...'
                  : decisaoTarget.decisao === 'APROVADA'
                    ? 'Confirmar aprovação'
                    : 'Confirmar negativa'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AutorizacoesTab;
